/* eslint-disable no-console */
import { useEffect } from "react";
import { useCallback } from "react";
import { useAuthenticationProvider } from "@/hooks/authentication/useAuthenticationProvider";
import { useHubUser } from "@/hooks/user/useHubUser";
import { useUserPartners } from "@/hooks/user/useUserPartners";
import { useUserSources } from "@/hooks/user/useUserSources";
import { mixpanelClient } from "@/lib/mixpanel";
import { useTrackWalletConnectStore } from "@/store/trackWalletConnectStore";
import * as Sentry from "@sentry/nextjs";

const ORGANIC_UTM = "organic";

/**
 * @description This hook is used to identify the user and track the wallet connection event
 * @returns void
 */
export const useTrackWalletConnect = () => {
  const { userInitiatedLogin, setUserInitiatedLogin, isNewUser, setIsNewUser } = useTrackWalletConnectStore();
  const { authenticated } = useAuthenticationProvider();
  const { hubUser, identityProvider, userFriendlyDefiAddress } = useHubUser();
  const { getSources, getCurrentSource } = useUserSources();
  const { getCurrentPartner } = useUserPartners();

  const walletAddress = hubUser?.defiAddress;
  const userId = hubUser?.id;

  const trackWalletConnection = useCallback(() => {
    if (walletAddress && userInitiatedLogin && isNewUser !== null) {
      mixpanelClient.trackWalletConnect();
      setUserInitiatedLogin(false);
      setIsNewUser(null);
      if (identityProvider === "ton") {
        mixpanelClient.registerOnce({
          connectedTon: true,
        });
      }
    }
  }, [walletAddress, userInitiatedLogin, isNewUser, identityProvider]);

  useEffect(() => {
    if (!authenticated || !walletAddress || !userId) {
      Sentry.setUser(null);
      return;
    }

    Sentry.setUser({
      id: userFriendlyDefiAddress ?? String(userId),
      username: userFriendlyDefiAddress,
    });

    mixpanelClient.identify(userFriendlyDefiAddress);

    mixpanelClient.setPeople({
      hubUserId: userId,
      identityProvider,
      rawDefiAddress: hubUser.defiAddress,
    });

    const currentSource = getCurrentSource();
    if (currentSource) {
      // Set the initial source UTM from the URL - only once per distinct id
      mixpanelClient.setPeopleOnce({
        initialSourceUtm: currentSource,
      });
      // Union the sources UTM from the URL - we don't include sources that are also stored in local storage
      mixpanelClient.unionPeople({
        sourcesUtmFromUrl: currentSource,
      });
    } else {
      mixpanelClient.setPeopleOnce({
        initialSourceUtm: ORGANIC_UTM,
      });
    }

    // Union the sources UTM from local storage and the URL - will be shared among users that are using the same device (browser)
    mixpanelClient.unionPeople({
      sourcesUtm: getSources(),
    });

    // Union the partner from URL or TMA startParam
    const partner = getCurrentPartner();
    if (partner) {
      mixpanelClient.unionPeople({
        partner,
      });
    }

    trackWalletConnection();
  }, [
    authenticated,
    walletAddress,
    userId,
    userInitiatedLogin,
    isNewUser,
    userFriendlyDefiAddress,
    getCurrentSource,
    getSources,
    getCurrentPartner,
  ]);

  return;
};
